import * as React from "react";
import { Button } from "@jhool-io/fe-components";
import { Navigate, useLocation } from "react-router-dom";
import useAuthContext from "../hooks/useAuthContext";
import { useFetchUserDetails } from "../hooks/queries/user";
import { useAppSelector } from "../hooks/useRedux";
import Loader from "../shared-ui/Loader/Loader";
import MfaEnrollment from "../modules/auth/pages/MfaEnrollment";
import { getInternalMfaChallengeMode } from "../modules/auth/utils/mfa";
import { useInternalMfaStatus } from "../modules/auth/hooks/mfa.queries";
import NotificationProvider from "../context/notification/NotificationProvider";
import PinnedClientsProvider from "../context/pinned-clients/PinnedClientsProvider";
import AnnouncementProvider from "../context/announcement/AnnouncementProvider";
import AuthWrapper from "../modules/auth/components/AuthWrapper/AuthWrapper";

function AuthenticatedContent({ children }: { children: JSX.Element }) {
    const { user, logOutUser } = useAuthContext();
    const { isFetched, isFetching } = useFetchUserDetails();
    const { practice } = useAppSelector((state) => state.userPractice);

    const location = useLocation();
    const hasTriggeredLogout = React.useRef(false);
    const isUserDetailsLoading = Boolean(user) && !isFetched;
    const isPracticeLoading = Boolean(user) && !practice;

    React.useEffect(() => {
        if (!user) {
            return;
        }
        if (
            !practice &&
            isFetched &&
            !isFetching &&
            !hasTriggeredLogout.current
        ) {
            hasTriggeredLogout.current = true;
            logOutUser().catch(() => undefined);
        }
    }, [isFetched, isFetching, logOutUser, practice, user]);

    React.useEffect(() => {
        if (!user) {
            return;
        }

        localStorage.setItem("from", `${location.pathname}${location.search}`);
    }, [location.pathname, location.search, user]);

    if (isUserDetailsLoading || isPracticeLoading) {
        return <Loader loaderType="page" />;
    }

    return (
        <NotificationProvider>
            <PinnedClientsProvider>
                <AnnouncementProvider>{children}</AnnouncementProvider>
            </PinnedClientsProvider>
        </NotificationProvider>
    );
}

export function RequireAuth({ children }: { children: JSX.Element }) {
    const { user, logOutUser } = useAuthContext();
    const { data, error, isLoading, isError, refetch } = useInternalMfaStatus(
        user?.uid
    );

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (isError) {
        return (
            <AuthWrapper footer={<span />}>
                <div className="space-y-[--spacing-form-items]">
                    <h1 className="text-titleLg font-bold text-text">
                        We could not verify your account security
                    </h1>
                    <p className="text-danger">
                        {error?.response?.data.message ||
                            "Please retry before continuing to Mantle."}
                    </p>
                    <Button className="w-full" onClick={() => refetch()}>
                        Retry
                    </Button>
                    <Button
                        className="w-full"
                        onClick={() => logOutUser()}
                        variant="secondary"
                    >
                        Sign out
                    </Button>
                </div>
            </AuthWrapper>
        );
    }

    if (isLoading || !data) {
        return <Loader loaderType="page" />;
    }

    const challengeMode = getInternalMfaChallengeMode(data);
    if (challengeMode !== "none") {
        return (
            <MfaEnrollment
                mode={challengeMode}
                onSignOut={logOutUser}
                onVerified={async () => {
                    await refetch();
                }}
            />
        );
    }

    return <AuthenticatedContent>{children}</AuthenticatedContent>;
}
